import {
  USER_DETAIL_TABS,
  type AdminUserDetail,
  type UserDetailTab,
} from '@steado/admin-contract/user-detail';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BadgeCell } from '@/components/table/cells';
import { useAuth } from '@/lib/auth/auth-context';
import { planTone } from '@/lib/plan-tone';

import { CoachTab } from './CoachTab';
import { EntitlementActions } from './EntitlementActions';
import { LogsTab } from './LogsTab';
import { PlanTab } from './PlanTab';
import { ProfileTab } from './ProfileTab';
import { RhythmTab } from './RhythmTab';
import { AiCostTab } from './AiCostTab';
import { ScansTab } from './ScansTab';
import { TabError } from './TabError';
import { TabSkeleton } from '@/components/panel/panels';
import { RecordHeader } from '@/components/panel/RecordHeader';
import { ScopePills } from '@/components/table/ScopePills';
import { formatDate, formatDateTime, formatPercent, formatRelative } from '@/lib/format';
import { userDetailQueryOptions } from './user-detail-query';
import type { UserDetailSearch } from './user-detail-search';

const TAB_LABELS: Readonly<Record<UserDetailTab, string>> = {
  profile: 'Profile',
  plan: 'Plan',
  logs: 'Logs',
  coach: 'Coach',
  scans: 'Scans',
  ai: 'AI cost',
  rhythm: 'Rhythm',
};

export interface UserDetailScreenProps {
  readonly userId: string;
  readonly search: UserDetailSearch;
  readonly onSearchChange: (next: UserDetailSearch) => void;
}

export function UserDetailScreen({ userId, search, onSearchChange }: UserDetailScreenProps) {
  const { api } = useAuth();
  const query = useQuery(userDetailQueryOptions(api, userId));

  const onSelectTab = useCallback(
    (tab: UserDetailTab) => {
      // Changing tab drops the previous tab's paging and open transcript: they
      // are that tab's position, not this one's.
      onSearchChange({ tab, cursor: undefined, session: undefined });
    },
    [onSearchChange],
  );

  const onCursorChange = useCallback(
    (cursor: string | undefined) => {
      onSearchChange({ ...search, cursor });
    },
    [search, onSearchChange],
  );

  const onOpenSession = useCallback(
    (session: string | undefined) => {
      onSearchChange({ ...search, session });
    },
    [search, onSearchChange],
  );

  if (query.isError) {
    return <TabError message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  return (
    <div className="flex max-w-[1160px] flex-col gap-3">
      {query.isPending ? <TabSkeleton rows={2} /> : <DetailHeader detail={query.data} />}

      <div className="flex flex-col gap-3">
        {/*
          Pills, not an underline tablist — the same control the scope rows
          use, which is what the design draws. `ScopePills` is a group of
          `aria-pressed` buttons rather than tabs: these swap which request the
          screen makes, and nothing is pre-rendered behind them to tab into.
        */}
        <ScopePills
          label="User detail"
          scopes={USER_DETAIL_TABS.map((tab) => ({
            id: tab,
            label: TAB_LABELS[tab],
            ...(tab === 'ai' || tab === 'rhythm' ? { isNew: true } : {}),
          }))}
          value={search.tab}
          onChange={onSelectTab}
        />

        <div>
          {/*
            Only the open tab is mounted. Each tab is its own request, and
            mounting all seven would fire seven on arrival to render one.
          */}
          {search.tab === 'profile' ? (
            query.isPending ? (
              <TabSkeleton rows={5} />
            ) : (
              <ProfileTab detail={query.data} />
            )
          ) : null}
          {search.tab === 'plan' && query.data !== undefined ? (
            <PlanTab userId={userId} hold={query.data.planSafetyHold} />
          ) : null}
          {search.tab === 'logs' ? <LogsTab userId={userId} /> : null}
          {search.tab === 'coach' ? (
            <CoachTab userId={userId} sessionId={search.session} onOpenSession={onOpenSession} />
          ) : null}
          {search.tab === 'scans' ? (
            <ScansTab userId={userId} cursor={search.cursor} onCursorChange={onCursorChange} />
          ) : null}
          {search.tab === 'ai' ? (
            <AiCostTab
              userId={userId}
              days={search.aiDays}
              onDaysChange={(aiDays) => {
                onSearchChange({ ...search, aiDays });
              }}
            />
          ) : null}
          {search.tab === 'rhythm' ? <RhythmTab userId={userId} /> : null}
        </div>
      </div>
    </div>
  );
}

function DetailHeader({ detail }: { readonly detail: AdminUserDetail }) {
  const { adherence } = detail;

  return (
    <RecordHeader
      // A named region rather than a bare `header`: the same facts appear again
      // inside the Profile tab, and "the summary" needs to be addressable.
      label="User summary"
      title={detail.displayName ?? 'Unnamed'}
      initials={initialsOf(detail.displayName)}
      badges={
        <>
          <BadgeCell label={detail.entitlement} tone={detail.entitlement === 'premium' ? 'purple' : 'mute'} />
          <BadgeCell label={detail.status} tone={detail.status === 'active' ? 'ok' : 'mute'} dot />
          {detail.planStatus === null ? null : (
            <BadgeCell label={`plan: ${detail.planStatus}`} tone={planTone(detail.planStatus)} />
          )}
          {detail.gyms.memberships
            .filter((membership) => membership.status === 'active')
            .map((membership) => (
              <BadgeCell key={membership.membershipId} label={`gym: ${membership.gym.name}`} tone="info" />
            ))}
          {detail.gyms.trainerRoles.some((role) => role.status === 'active') ? (
            <BadgeCell label="trainer" tone="purple" />
          ) : null}
        </>
      }
      actions={<EntitlementActions userId={detail.id} subscription={detail.subscription} />}
      meta={[
        detail.phone,
        detail.timezone,
        `Joined ${formatDate(detail.createdAt)}`,
        detail.lastActiveAt === null ? 'Never active' : `Last active ${formatRelative(detail.lastActiveAt)}`,
      ]}
      stats={[
        {
          label: 'Days logged',
          value: `${String(adherence.daysLogged)}/${String(adherence.windowDays)}`,
          note: `${formatPercent(adherence.mealLogRate)} of the window`,
        },
        {
          label: 'Workouts',
          value:
            adherence.workoutsPlanned === 0
              ? String(adherence.workoutsCompleted)
              : `${String(adherence.workoutsCompleted)}/${String(adherence.workoutsPlanned)}`,
          // Null is not zero: nothing was planned, so there is no rate to give.
          note: adherence.workoutRate === null ? 'none planned' : formatPercent(adherence.workoutRate),
        },
        {
          label: 'Last active',
          value: detail.lastActiveAt === null ? '—' : formatRelative(detail.lastActiveAt),
          note: detail.lastActiveAt === null ? 'never' : formatDateTime(detail.lastActiveAt),
        },
        {
          label: 'Onboarded',
          value: detail.onboardedAt === null ? '—' : formatDate(detail.onboardedAt),
          note: detail.onboardedAt === null ? 'never finished' : 'finished the questions',
        },
      ]}
    />
  );
}

/** Two letters for the avatar; "??" when there is no name to take them from. */
function initialsOf(name: string | null): string {
  const trimmed = name?.trim() ?? '';
  if (trimmed === '') return '??';

  const parts = trimmed.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
